import {
  ApiError,
  CreateTimetableResponse,
  GetTimetableResponse,
  ModifyTimetableRequest,
  ModifyTimetableResponse,
  SchoolYear,
  TimetableStructure,
  useCreateTimetableMutation,
  useDeleteTimetableMutation,
  useGetTimetableQuery,
  useModifyTimetableMutation,
  usePerformTimetableActionMutation,
} from '@schooly/api';
import { TimetablePerformActionEnum } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { isErrorByDetails } from '@schooly/utils/set-form-error-for-api-error';
import { createContext, FC, PropsWithChildren, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';

export const TIMETABLE_NAME_UNIQUENESS_ERROR = 'TIMETABLE_NAME_UNIQUENESS_ERROR';

export type TimetableContextProps = {
  timetable?: GetTimetableResponse;
  id?: string;
  isFetching: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isPerformingAction: boolean;
  createTimetable: (
    schoolYearId: string,
    slotAllocations: ModifyTimetableRequest['data']['slot_allocations'],
    name: string,
  ) => Promise<CreateTimetableResponse>;
  updateTimetable: (
    timetableId: string,
    data: ModifyTimetableRequest['data'],
  ) => Promise<ModifyTimetableResponse>;
  removeTimetable: (timetableId: string) => Promise<void>;
  performTimetableAction: (
    timetableId: string,
    action: TimetablePerformActionEnum,
  ) => Promise<void>;
  structure: TimetableStructure | GetTimetableResponse['structure'];
  schoolYear: SchoolYear;
  schoolId: string;
};

const TimetableContext = createContext<TimetableContextProps>({
  timetable: undefined,
  id: undefined,
  isFetching: false,
  isSaving: false,
  isDeleting: false,
  isPerformingAction: false,
  createTimetable: async () => ({}) as CreateTimetableResponse,
  updateTimetable: async () => ({}) as ModifyTimetableResponse,
  removeTimetable: async () => {},
  performTimetableAction: async () => {},
  structure: {} as TimetableStructure | GetTimetableResponse['structure'],
  schoolYear: {} as SchoolYear,
  schoolId: '',
});

export const WithTimetable: FC<
  PropsWithChildren & {
    timetable?: GetTimetableResponse;
    structure: TimetableStructure | GetTimetableResponse['structure'];
    schoolYear: SchoolYear;
  }
> = ({ children, timetable: providedTimetable, structure, schoolYear }) => {
  const { id = '' } = useParams<'id'>();
  const { schoolId = '' } = useAuth();
  const { showError } = useNotifications();

  const createTimetable = useCreateTimetableMutation();
  const modifyTimetable = useModifyTimetableMutation();
  const deleteTimetable = useDeleteTimetableMutation();
  const performTimetableAction = usePerformTimetableActionMutation();

  const { data, isFetching } = useGetTimetableQuery(
    { timetableId: id },
    {
      enabled: !!id && !providedTimetable,
      refetchOnMount: 'always',
      onError: showError,
    },
  );

  const timetable = providedTimetable ?? data;

  const handleTimetableCreate = useCallback(
    async (
      schoolYearId: string,
      slotAllocations: ModifyTimetableRequest['data']['slot_allocations'],
      name: string,
    ): Promise<CreateTimetableResponse> => {
      if (!schoolId) throw new Error('School ID is required');

      try {
        return await createTimetable.mutateAsync({
          school_id: schoolId,
          structure_id: structure.id,
          school_year_id: schoolYearId,
          slot_allocations: slotAllocations,
          name,
        });
      } catch (e) {
        if (!isErrorByDetails(e as ApiError, [TIMETABLE_NAME_UNIQUENESS_ERROR])) {
          showError(e as ApiError);
        }

        throw e;
      }
    },
    [schoolId, createTimetable, structure.id, showError],
  );

  const handleTimetableUpdate = useCallback(
    async (
      timetableId: string,
      requestData: ModifyTimetableRequest['data'],
    ): Promise<ModifyTimetableResponse> => {
      try {
        return await modifyTimetable.mutateAsync({
          params: { timetableId },
          data: requestData,
        });
      } catch (e) {
        if (!isErrorByDetails(e as ApiError, [TIMETABLE_NAME_UNIQUENESS_ERROR])) {
          showError(e as ApiError);
        }
        throw e;
      }
    },
    [modifyTimetable, showError],
  );

  const handleTimetableRemove = useCallback(
    async (timetableId: string) => {
      try {
        await deleteTimetable.mutateAsync({ timetableId });
      } catch (e) {
        showError(e as ApiError);
        throw e;
      }
    },
    [deleteTimetable, showError],
  );

  const handleTimetableAction = useCallback(
    async (timetableId: string, action: TimetablePerformActionEnum) => {
      try {
        await performTimetableAction.mutateAsync({
          params: { timetableId },
          data: { action },
        });
      } catch (e) {
        showError(e as ApiError);
        throw e;
      }
    },
    [performTimetableAction, showError],
  );

  return (
    <TimetableContext.Provider
      value={{
        id,
        timetable,
        structure,
        isFetching,
        createTimetable: handleTimetableCreate,
        updateTimetable: handleTimetableUpdate,
        removeTimetable: handleTimetableRemove,
        performTimetableAction: handleTimetableAction,
        isSaving: createTimetable.isLoading || modifyTimetable.isLoading,
        isDeleting: deleteTimetable.isLoading,
        isPerformingAction: performTimetableAction.isLoading,
        schoolYear,
        schoolId,
      }}
    >
      {children}
    </TimetableContext.Provider>
  );
};

export const useTimetable = () => {
  return useContext(TimetableContext);
};
